'use client';

import { Flex, Table, Text } from '@chakra-ui/react';
import { memo, useCallback } from 'react';
import { useRouter } from 'next/navigation';

import SupplierPayoutStatusBadge from '@features/finance/SupplierPayoutStatusBadge';
import { formatUsdc, SUPPLIER_PAYOUT_NETWORK_LABEL } from '@features/finance/financeMeta';
import { allPaymentRequests, allSupplierPayouts } from 'helpers/fakeData';
import { useHref } from 'hooks/useHref';

const requestTitleById = (id: string): string => {
  return allPaymentRequests.find((request) => request.id === id)?.title ?? '—';
};

const PayoutsPage = () => {
  const router = useRouter();
  const { payoutHref } = useHref();

  const onRowClick = useCallback(
    (payoutId: string) => () => {
      router.push(payoutHref(payoutId));
    },
    [router, payoutHref],
  );

  return (
    <Flex flexDirection="column" gap={4} p={4}>
      <Flex direction="column" gap={0.5}>
        <Text fontSize="lg" fontWeight="bold">
          Payouts
        </Text>
        <Text fontSize="sm" color="text.secondary">
          USDC sent to supplier wallets when payment links are fulfilled.
        </Text>
      </Flex>
      {allSupplierPayouts.length === 0 ? (
        <Flex
          direction="column"
          bg="surface.muted"
          borderRadius="xl"
          p={10}
          alignItems="center"
          gap={1}
        >
          <Text fontWeight="medium">No payouts yet</Text>
          <Text fontSize="sm" color="text.secondary">
            When a payment link is fulfilled, the supplier payout shows up here.
          </Text>
        </Flex>
      ) : (
      <Flex direction="column" bg="surface.muted" borderRadius="xl" px={2} pb={2}>
        <Table.Root>
          <Table.Header fontSize="sm" fontWeight="medium">
            <Table.Row bg="transparent">
              <Table.ColumnHeader border="none" color="text.secondary" fontWeight="medium" p={4}>
                Supplier
              </Table.ColumnHeader>
              <Table.ColumnHeader
                border="none"
                color="text.secondary"
                fontWeight="medium"
                p={4}
                hideBelow="md"
              >
                From link
              </Table.ColumnHeader>
              <Table.ColumnHeader
                border="none"
                color="text.secondary"
                fontWeight="medium"
                p={4}
                hideBelow="md"
              >
                Network
              </Table.ColumnHeader>
              <Table.ColumnHeader
                border="none"
                color="text.secondary"
                fontWeight="medium"
                p={4}
                textAlign="end"
              >
                Amount
              </Table.ColumnHeader>
              <Table.ColumnHeader
                border="none"
                color="text.secondary"
                fontWeight="medium"
                p={4}
                textAlign="end"
              >
                Status
              </Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {allSupplierPayouts.map((payout, index) => (
              <Table.Row
                key={payout.id}
                fontSize="sm"
                cursor="pointer"
                onClick={onRowClick(payout.id)}
              >
                <Table.Cell
                  border="none"
                  p={4}
                  borderTopLeftRadius={index === 0 ? 'xl' : 'none'}
                  borderBottomLeftRadius={index === allSupplierPayouts.length - 1 ? 'xl' : 'none'}
                >
                  <Text fontWeight="medium">{payout.supplierName}</Text>
                </Table.Cell>
                <Table.Cell border="none" p={4} hideBelow="md" color="text.secondary">
                  {requestTitleById(payout.paymentRequestId)}
                </Table.Cell>
                <Table.Cell border="none" p={4} hideBelow="md">
                  {SUPPLIER_PAYOUT_NETWORK_LABEL[payout.network]}
                </Table.Cell>
                <Table.Cell border="none" p={4} textAlign="end" fontWeight="medium">
                  {formatUsdc(payout.amountUsdc)}
                </Table.Cell>
                <Table.Cell
                  border="none"
                  p={4}
                  borderTopRightRadius={index === 0 ? 'xl' : 'none'}
                  borderBottomRightRadius={index === allSupplierPayouts.length - 1 ? 'xl' : 'none'}
                >
                  <Flex justifyContent="flex-end">
                    <SupplierPayoutStatusBadge status={payout.status} />
                  </Flex>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      </Flex>
      )}
    </Flex>
  );
};

export default memo(PayoutsPage);
